import React, { useState, useCallback } from "react";
import styled from "styled-components";
import { CSSTransition } from 'react-transition-group';
import { useTranslation } from "react-i18next";

import Divider from "../core/Divider";
import Flex from "../core/Flex";
import Space from "../core/Space";
import FormText from "../form/FormText";
import useViewStore from "../../store/useViewStore";
import useApplyNewStore from "../../store/useApplyNewStore";

const NavStepControl = ({ children }) => {
  const { t } = useTranslation();
  const { step, setStep } = useApplyNewStore();
  const { saveFormStep1, saveFormStep2, saveFormStep3, getPreAuthFormForStep4 } = useApplyNewStore();
  const { isStep1Valid, step2_upload_option1, step2_upload_option2, isStep3Valid } = useApplyNewStore();
  const nodeRef = React.useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleBlur = (e) => {
    setIsOpen(false);
  };

  const handleClick = useCallback((e) => {
    if (isOpen) {
      e.currentTarget.blur();
    } else {
      e.currentTarget.focus();
    }

    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
  }, [isOpen]);

  const saveForm = async () => {
    if (step === "1") {
      await saveFormStep1();
    } else if (step === "2") {
      await saveFormStep2();
    } else if (step === "3") {
      await saveFormStep3();
    }
  }

  return (
    <NavControlContainer>
      <NavControl onClick={handleClick} onBlur={handleBlur}>
        {
          isStep1(step) &&
          <>
            <NavStepActive>1</NavStepActive>
            <NavTitleActive style={{ margin: 0 }}>{t("translation:apply.nav.Step1Title")}</NavTitleActive>
          </>
        }
        {
          isStep2(step) &&
          <>
            <NavStepActive>2</NavStepActive>
            <NavTitleActive style={{ margin: 0 }}>{t("translation:apply.nav.Step2Title")}</NavTitleActive>
          </>
        }
        {
          isStep3(step) &&
          <>
            <NavStepActive>3</NavStepActive>
            <NavTitleActive style={{ margin: 0 }}>{t("translation:apply.nav.Step3Title")}</NavTitleActive>
          </>
        }
        {
          isStep4(step) &&
          <>
            <NavStepActive>4</NavStepActive>
            <NavTitleActive style={{ margin: 0 }}>{t("translation:apply.nav.Step4Title")}</NavTitleActive>
          </>
        }
        <Dots3 src={`${process.env.PUBLIC_URL}/images/dots-3-vertical.svg`} style={{ width: 16, height: 16, marginTop: 3, marginLeft: 3 }} alt="dots" />
      </NavControl>

      <CSSTransition
        nodeRef={nodeRef}
        in={isOpen}
        timeout={250}
        classNames="fade"
        unmountOnExit
      >
        <DropdownNavControl ref={nodeRef}>
          <NavItemButton onClick={async () => {
            await saveForm();
            setIsOpen(false);
            setStep("1");
          }}>
            {
              isStep1(step) ?
                <>
                  <NavStepActive>1</NavStepActive>
                  <NavTitleActive>{t("translation:apply.nav.Step1Title")}</NavTitleActive>
                </>
                :
                <>
                  <NavStep>1</NavStep>
                  <NavTitle>{t("translation:apply.nav.Step1Title")}</NavTitle>
                </>
            }
          </NavItemButton>
          <NavItemButton $disabled={!isStep1Valid} onClick={async () => {
            if (isStep1Valid) {
              await saveForm();
              setIsOpen(false);
              setStep("2");
            }
          }}>
            {
              isStep2(step) ?
                <>
                  <NavStepActive>2</NavStepActive>
                  <NavTitleActive>{t("translation:apply.nav.Step2Title")}</NavTitleActive>
                </>
                :
                <>
                  <NavStep>2</NavStep>
                  <NavTitle>{t("translation:apply.nav.Step2Title")}</NavTitle>
                </>
            }
          </NavItemButton>
          <NavItemButton $disabled={!step2_upload_option1 && !step2_upload_option2} onClick={async () => {
            if (!!step2_upload_option1 || !!step2_upload_option2) {
              await saveForm();
              setIsOpen(false);
              setStep("3");
            }
          }}>
            {
              isStep3(step) ?
                <>
                  <NavStepActive>3</NavStepActive>
                  <NavTitleActive>{t("translation:apply.nav.Step3Title")}</NavTitleActive>
                </>
                :
                <>
                  <NavStep>3</NavStep>
                  <NavTitle>{t("translation:apply.nav.Step3Title")}</NavTitle>
                </>
            }
          </NavItemButton>
          <NavItemButton $disabled={!isStep3Valid} onClick={async () => {
            if (isStep3Valid) {
              await saveForm();
              await getPreAuthFormForStep4();
              setIsOpen(false);
              setStep("4");
            }
          }}>
            {
              isStep4(step) ?
                <>
                  <NavStepActive>4</NavStepActive>
                  <NavTitleActive>{t("translation:apply.nav.Step4Title")}</NavTitleActive>
                </>
                :
                <>
                  <NavStep>4</NavStep>
                  <NavTitle>{t("translation:apply.nav.Step4Title")}</NavTitle>
                </>
            }
          </NavItemButton>
        </DropdownNavControl>
      </CSSTransition>
    </NavControlContainer>
  );
};

const isStep1 = (step) => step === "1";
const isStep2 = (step) => step === "2";
const isStep3 = (step) => step === "3";
const isStep4 = (step) => step === "4";

const isInStep2OrAfter = (step) => {
  return step === "2_1" || step === "2_2" || step === "2_3" || step === "2_4" || step === "2_5" || step === "3_1" || step === "4_1" || step === "success";
}

const NavControlContainer = styled.div`
  position: relative;
`;

const NavControl = styled.button`
  border-radius: 50px;
  padding: 8px 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid var(--aia-bx-grey-20);
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.3);
  background-color: var(--aia-bx-gray-6);

  &:hover {
    background-color: var(--aia-bx-bright-blue-5);
  }
`;

const NavItemButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 6px;
  border-radius: 10px;
  width: 100%;
  justify-content: flex-start;

  &:hover {
    ${props => props.$disabled ? `cursor: not-allowed;` : `background-color: var(--aia-bx-bright-blue-5);`}
  }
`;

const NavStep = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--aia-bx-gray-5);
  color: var(--aia-bx-white);
  height: 32px;
  width: 32px;
  line-height: 0px;
  border-radius: 50%;
`;

const NavStepActive = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--aia-bx-blue-2);
  color: var(--aia-bx-white);
  height: 32px;
  width: 32px;
  line-height: 0px;
  border-radius: 50%;
`;

const NavTitle = styled.div`
  margin-left: 10px;
  font-size: 17px;
  font-weight: bold;
  text-align: left;
  color: var(--aia-bx-gray-50);
  flex: 1;
`;

const NavTitleActive = styled.div`
  margin-left: 10px;
  font-size: 17px;
  font-weight: bold;
  text-align: left;
  color: var(--aia-bx-dark-blue);
  flex: 1;
`;

const Dots3 = styled.img`
  width: 24px;
  height: 24px;
`;

const DropdownNavControl = styled.div`
  position: absolute;
  top: 60px;
  left: 0px;
  width: 300px;
  border: 1px solid var(--aia-bx-grey-20);
  background-color: var(--aia-bx-gray-6);
  box-shadow: 0px 0px 0.5rem 0px rgba(2, 114, 174, 0.2);
  padding: 5px 8px;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: self-start;
`;

export default NavStepControl;